import momentTz from 'moment-timezone';
import _ from 'underscore';
import { Timezone, Weekday, WeekdayHelper } from '@biteinc/enums';
import { UniversalTime } from '@biteinc/helpers-universal';
import * as StringHelper from './string_helper.js';
export const BITE_TZ = Timezone.AmericaNewYork;
export const SECOND = UniversalTime.SECOND;
export const MINUTE = UniversalTime.MINUTE;
export const HOUR = UniversalTime.HOUR;
export const DAY = UniversalTime.DAY;
export const WEEK = UniversalTime.WEEK;
export const DAYS = UniversalTime.DAYS;
export const FULL_DAYS = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
// These are listed in order of a typical working week
export const WORK_WEEK_DAYS = [
    Weekday.Mon,
    Weekday.Tue,
    Weekday.Wed,
    Weekday.Thu,
    Weekday.Fri,
    Weekday.Sat,
    Weekday.Sun,
];
export const WORK_WEEK_FULL_DAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];
// A restaurant day starts at 4am instead of midnight
export const DAY_START_HOURS = 4;
export const DAY_START_MINUTES = 0;
export const DAY_START = DAY_START_HOURS * HOUR + DAY_START_MINUTES * MINUTE;
export const ORDER_IS_ABANDONED_AFTER = 2 * HOUR;
// Technically this returns any possible timezone in the world and
// not just those defined in our Timezone enum.
export function guess() {
    return momentTz.tz.guess();
}
export function zone(timezone) {
    return momentTz.tz.zone(timezone);
}
export function utc(timestamp, format) {
    return momentTz.utc(timestamp, format);
}
export function moment(timestamp, timezone = BITE_TZ) {
    return momentTz.tz(timestamp, timezone);
}
export function timestampAtSameLocalTimePreviousWeek(timestamp, timezone) {
    return moment(timestamp, timezone).subtract(1, 'week').valueOf();
}
export function momentWithFormat(timestamp, format, timezone = BITE_TZ) {
    return momentTz.tz(timestamp, format, timezone);
}
export function minsToMs(numberOfMinutes) {
    return (numberOfMinutes || 0) * MINUTE;
}
export function msToS(timeInMs) {
    return Math.floor(timeInMs / SECOND);
}
// Rounds up so partial minutes are never understated (30s -> 1 min)
export function secsToMins(numberOfSeconds) {
    return Math.ceil((numberOfSeconds * SECOND) / MINUTE);
}
export function isAfterLocalMidnight(possibleTodayMoment, timezone) {
    const endOfToday = momentTz.tz(timezone).endOf('day');
    return !possibleTodayMoment.isSameOrBefore(endOfToday);
}
export function next4Am(timezone) {
    const now = momentTz.tz(Date.now(), timezone);
    const fourAmToday = momentTz.tz(Date.now(), timezone).startOf('day').add(4, 'hours');
    if (now.isBefore(fourAmToday)) {
        return fourAmToday.valueOf();
    }
    return fourAmToday.add(1, 'day').valueOf();
}
export function stringFromTimestamp(time, timezone = BITE_TZ, { short = false, includeYear = false, } = {}) {
    return momentTz
        .tz(time, timezone)
        .format(`${short ? '' : 'ddd '}${includeYear ? 'YYYY/' : ''}MM/DD HH:mm:ss z`);
}
export const timestampWithRounding = UniversalTime.timestampWithRounding;
export function roundToLowerByRoundingFactor(time, minRoundingFactor) {
    const ROUNDING_FACTOR = minRoundingFactor * MINUTE;
    return moment(Math.floor(+time / ROUNDING_FACTOR) * ROUNDING_FACTOR);
}
export function roundToNext15(timeMoment) {
    return moment(UniversalTime.roundUnixTimeToNext15(timeMoment.valueOf()));
}
export function toDayTimestamp(date) {
    return date.hour() * HOUR + date.minute() * MINUTE + date.second() * SECOND + date.millisecond();
}
export const weekTimestampWithRounding = UniversalTime.weekTimestampWithRounding;
export const weekTimestamp = UniversalTime.weekTimestamp;
export function monthTimestamp(time, timezone) {
    const timeM = momentTz.tz(time, timezone);
    // Subtract a day so that the date (aka, day of the month) is 0-indexed
    return (timeM.date() - 1) * DAY + toDayTimestamp(timeM);
}
export function weekTimestampRoundedToNearest15Mins(time, timezone) {
    return weekTimestampWithRounding(time, timezone, MINUTE * 15);
}
export const timestampRoundedToNearest15Mins = UniversalTime.timestampRoundedToNearest15Mins;
export const dayTimestampFromTime = UniversalTime.dayTimestampFromTime;
export function timestampRoundedToNearest30Mins(time) {
    return timestampWithRounding(time, MINUTE * 30);
}
export function weekTimestampsForOneDay(interval = MINUTE * 15, start = HOUR * 9) {
    const timestamps = [];
    for (let i = start; i < start + DAY; i += interval) {
        timestamps.push(i % DAY);
    }
    return timestamps;
}
export function parse(timestamp, format, timezone) {
    return momentTz.tz(timestamp, format, timezone || BITE_TZ);
}
export function strictParse(timestamp, format, timezone) {
    return momentTz.tz(timestamp, format, true, timezone || BITE_TZ);
}
export function formatForEng(timestamp) {
    return moment(timestamp, BITE_TZ).format('MM/DD/YY HH:mm z');
}
export function getDuration(durationMs) {
    return momentTz.duration(durationMs);
}
export function stringFromDuration(durationMs) {
    const parts = [];
    const duration = momentTz.duration(durationMs);
    if (duration.days() > 0) {
        parts.push(`${duration.days()}d`);
    }
    if (duration.hours() > 0) {
        parts.push(`${duration.hours()}hr`);
    }
    if (duration.minutes() > 0) {
        parts.push(`${duration.minutes()}min`);
    }
    const seconds = duration.seconds() + (duration.milliseconds() > 0 ? 1 : 0);
    if (seconds > 0) {
        parts.push(`${seconds}sec`);
    }
    return parts.length ? parts.join(' ') : '0';
}
export function formatDowntimeDuration(minutes, options) {
    if (minutes < 60) {
        return options.compact ? `${minutes}m` : `${minutes} minutes`;
    }
    if (minutes < 1440) {
        const hours = Math.floor(minutes / 60);
        if (options.compact) {
            return `${hours}h`;
        }
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    const days = Math.floor(minutes / 1440);
    if (options.compact) {
        return `${days}d`;
    }
    return `${days} ${days === 1 ? 'day' : 'days'}`;
}
export function stringTimeFromDayTimeStamp(dayTimestamp) {
    let timeString = '';
    const duration = momentTz.duration(dayTimestamp);
    timeString += `${duration.hours() % 12 || 12}:`;
    if (duration.minutes() > 0) {
        timeString += `${duration.minutes()}`.padStart(2, '0');
    }
    else {
        timeString += '00';
    }
    timeString += duration.hours() < 12 ? 'am' : 'pm';
    return timeString;
}
export const getDayFromWeekTimestamp = UniversalTime.getDayFromWeekTimestamp;
export function getDayFromTimestamp(time, timezone) {
    const timeM = moment(time, timezone);
    return getDayFromMoment(timeM);
}
export function getDayFromMoment(timeMoment) {
    return DAYS[timeMoment.day()];
}
export function countWeekdaysBetweenDates(dateRange) {
    const counts = {
        [Weekday.Sun]: 0,
        [Weekday.Mon]: 0,
        [Weekday.Tue]: 0,
        [Weekday.Wed]: 0,
        [Weekday.Thu]: 0,
        [Weekday.Fri]: 0,
        [Weekday.Sat]: 0,
    };
    const fromDate = moment(dateRange.from, BITE_TZ).startOf('day');
    const toDate = moment(dateRange.to, BITE_TZ).startOf('day');
    let currDate = fromDate.clone();
    while (currDate.isSameOrBefore(toDate)) {
        const weekday = getDayFromMoment(currDate);
        counts[`${weekday}`] = Number(counts[`${weekday}`]) + 1;
        currDate.add(1, 'day');
    }
    return counts;
}
export const getDayTimestampFromWeekTimestamp = UniversalTime.getDayTimestampFromWeekTimestamp;
export function getDayTimestampFromMoment(timeMoment) {
    const midnightTimestamp = timeMoment.clone().startOf('day').valueOf();
    const diff = timeMoment.valueOf() - midnightTimestamp;
    return diff;
}
export function dateWithOffsetFromDayTimestamp(dayTimestamp) {
    const date = new Date(dayTimestamp);
    let time = date.getTime() + date.getTimezoneOffset() * MINUTE;
    if (time < 0) {
        time += DAY;
    }
    date.setTime(time);
    return date;
}
export function datesBelongToDifferentDays(m1, m2) {
    return m1.format('DD.MM.YYYY') !== m2.format('DD.MM.YYYY');
}
export function timestampsBelongToDifferentDays(timestamp1, timestamp2, timezone) {
    return datesBelongToDifferentDays(moment(timestamp1, timezone), moment(timestamp2, timezone));
}
// Takes a list of Weekdays [DAYS here] and returns friendly format Mon-Wed, Fri
export function dateStringsFromWeekdays(weekdays) {
    if (weekdays.length === 1) {
        return StringHelper.toTitleCase(weekdays[0]);
    }
    const arrayOfDayIndexes = weekdays.map((day) => {
        return WORK_WEEK_DAYS.indexOf(day);
    });
    arrayOfDayIndexes.sort();
    const days = [];
    let lastStartIndex = -1;
    let previousDayIndex;
    for (const [index, dayIndex] of arrayOfDayIndexes.entries()) {
        if (lastStartIndex === -1) {
            days.push(StringHelper.toTitleCase(WORK_WEEK_DAYS[dayIndex]));
            lastStartIndex = dayIndex;
        }
        else if (index === arrayOfDayIndexes.length - 1 ||
            arrayOfDayIndexes[index + 1] !== dayIndex + 1) {
            if (previousDayIndex === undefined || previousDayIndex + 1 !== dayIndex) {
                days.push(StringHelper.toTitleCase(WORK_WEEK_DAYS[dayIndex]));
            }
            else {
                days[days.length - 1] =
                    `${days.at(-1)}-${StringHelper.toTitleCase(WORK_WEEK_DAYS[dayIndex])}`;
            }
            lastStartIndex = -1;
        }
        else if (lastStartIndex === dayIndex - 1) {
            lastStartIndex++;
        }
        else if (index !== arrayOfDayIndexes.length - 1) {
            days.push(StringHelper.toTitleCase(WORK_WEEK_DAYS[dayIndex]));
            lastStartIndex = dayIndex;
        }
        previousDayIndex = dayIndex;
    }
    return days.join(', ');
}
export function getTimeBlockForTime(time, timeBlockLength) {
    // unix has 86400 ms in a day, regardless of leap seconds, etc
    // round to the nearest start and end of the time block
    // subtract 1 ms from the end so we avoid overlap
    const startAt = Math.floor(time / timeBlockLength) * timeBlockLength;
    const endAt = Math.ceil(time / timeBlockLength) * timeBlockLength - 1;
    return {
        startAt,
        // If time is a multiple of timeBlockLength, then endAt will be one less than start-at
        // Fix this by adding timeBlockLength to endAt
        endAt: endAt < startAt ? endAt + timeBlockLength : endAt,
    };
}
export const hourRangeFromSchedule = UniversalTime.hourRangeFromSchedule;
export function doesScheduleWrap(schedule) {
    if (schedule.to.hours === 0 && schedule.to.minutes === 0) {
        // A schedule ending at midnight does not wrap
        // ex: from = 19:30; to = 00:00
        return false;
    }
    if (schedule.from.hours < schedule.to.hours) {
        // ex: from = 12:15; to = 15:30
        return false;
    }
    if (schedule.to.hours < schedule.from.hours) {
        // ex: from = 23:00; to = 07:00
        return true;
    }
    // Hours are the same
    // ex: from = 16:30; to = 16:45 | Does not wrap
    // ex: from = 16:30; to = 16:15 | Wraps
    // If the boundaries are the same, but not midnight, then the schedule wraps
    // ex: from = 16:30; to = 16:30 | Wraps
    return schedule.to.minutes <= schedule.from.minutes;
}
export function scheduleBoundaryFromDayTimestamp(dayTimestamp) {
    const totalHours = Math.floor(dayTimestamp / HOUR);
    // Schedule boundaries have a max `hours` value of 23; Prevent it from overflowing to be safe.
    const hours = totalHours % 24;
    const remainder = dayTimestamp - totalHours * HOUR;
    const minutes = Math.floor(remainder / MINUTE);
    return { hours, minutes };
}
export function scheduleFromHourRange(hourRange) {
    return {
        from: scheduleBoundaryFromDayTimestamp(hourRange.from),
        to: scheduleBoundaryFromDayTimestamp(hourRange.to),
    };
}
export function parseWeekSchedule(fromDay, toDay, fromDayTs, toDayTs) {
    if (fromDayTs < 0 || DAY <= fromDayTs) {
        throw new Error(`Invalid From Day Timestamp: ${fromDayTs}`);
    }
    if (toDayTs < 0 || DAY <= toDayTs) {
        throw new Error(`Invalid To Day Timestamp: ${toDayTs}`);
    }
    const schedulesByWeekday = {};
    const fromDaySb = scheduleBoundaryFromDayTimestamp(fromDayTs);
    const toDaySb = scheduleBoundaryFromDayTimestamp(toDayTs);
    if (fromDay === toDay) {
        schedulesByWeekday[fromDay] = {
            from: fromDaySb,
            to: toDaySb,
        };
        return schedulesByWeekday;
    }
    schedulesByWeekday[fromDay] = {
        from: fromDaySb,
        // The first day ends at midnight
        to: { hours: 0, minutes: 0 },
    };
    const fromDayI = DAYS.findIndex((day) => {
        return day === fromDay;
    });
    const toDayI = DAYS.findIndex((day) => {
        return day === toDay;
    });
    let currDayI = (fromDayI + 1) % 7;
    while (currDayI !== toDayI) {
        const currDay = DAYS.at(currDayI);
        if (currDay === undefined) {
            throw new Error(`Invalid Weekday index: ${currDayI}`);
        }
        // This day is between the first and last day, so add a full 24 hour schedule
        schedulesByWeekday[currDay] = {
            from: { hours: 0, minutes: 0 },
            to: { hours: 0, minutes: 0 },
        };
        currDayI = (currDayI + 1) % 7;
    }
    schedulesByWeekday[toDay] = {
        // The last day starts at midnight
        from: { hours: 0, minutes: 0 },
        to: toDaySb,
    };
    return schedulesByWeekday;
}
export function parseWeekSchedulesMapToDaySchedulesArray(weekScheduleMap) {
    return DAYS.flatMap((weekday) => {
        const schedules = weekScheduleMap[weekday];
        return schedules?.length ? [{ weekday, schedules }] : [];
    });
}
export const dayTimestampFromScheduleBoundary = UniversalTime.dayTimestampFromScheduleBoundary;
export function differenceBetweenDayTimestamps(dayTimestampA, dayTimestampB) {
    const difference = Math.abs(dayTimestampA - dayTimestampB);
    if (difference > DAY / 2) {
        // This means we wrapped around the day boundary
        return Math.abs(difference - DAY);
    }
    return difference;
}
export const getDayAdjustedRanges = UniversalTime.getDayAdjustedRanges;
export const rangeThatIsActiveAt = UniversalTime.rangeThatIsActiveAt;
export function scheduleThatIsActiveAt(scheduleByWeekday, weekTs) {
    const rangeByWeekday = {};
    DAYS.forEach((day) => {
        const schedule = scheduleByWeekday[day];
        rangeByWeekday[day] = schedule ? hourRangeFromSchedule(schedule) : null;
    });
    const activeRange = rangeThatIsActiveAt(rangeByWeekday, weekTs);
    return activeRange ? scheduleFromHourRange(activeRange) : undefined;
}
export const scheduleIsActiveAt = UniversalTime.scheduleIsActiveAt;
export function dayTimeRangeSummary(dayTimeRanges) {
    const DaysStartingWithMonday = WeekdayHelper.allDaysStartingWithMonday();
    return (_.chain(dayTimeRanges)
        .sortBy((dayRange) => {
        return DaysStartingWithMonday.indexOf(dayRange.day);
    })
        .groupBy(({ ranges }) => {
        const sortedRanges = ranges.sort((rangeA, rangeB) => {
            return rangeA.from - rangeB.from || rangeA.to - rangeB.to;
        });
        return sortedRanges
            .map((range) => {
            return `${range.from}-${range.to}`;
        })
            .join(', ');
    })
        .values()
        // Sort by who has the earliest day
        .sortBy((dayTimeRangeGroup) => {
        const earliestDayTimeRange = _.min(dayTimeRangeGroup, (dayTimeRange) => {
            return DaysStartingWithMonday.indexOf(dayTimeRange.day);
        });
        return DaysStartingWithMonday.indexOf(earliestDayTimeRange.day);
    })
        .map((dayTimeRangeGroup) => {
        const days = [];
        let lastStartIndex = -1;
        for (const [index, dayTimeRange] of dayTimeRangeGroup.entries()) {
            const dayIndex = DaysStartingWithMonday.indexOf(dayTimeRange.day);
            let nextDayIndex;
            if (index < dayTimeRangeGroup.length - 1) {
                const nextDayTimeRange = dayTimeRangeGroup[index + 1];
                if (nextDayTimeRange === undefined) {
                    throw new Error(`Missing day range at index ${index + 1}`);
                }
                nextDayIndex = DaysStartingWithMonday.indexOf(nextDayTimeRange.day);
            }
            if (lastStartIndex === -1) {
                days.push(StringHelper.toTitleCase(dayTimeRange.day));
                lastStartIndex = dayIndex;
            }
            else if (index === dayTimeRangeGroup.length - 1 || nextDayIndex !== dayIndex + 1) {
                days[days.length - 1] = `${days.at(-1)}-${StringHelper.toTitleCase(dayTimeRange.day)}`;
                lastStartIndex = -1;
            }
            else if (lastStartIndex === dayIndex - 1) {
                lastStartIndex++;
            }
            else if (index !== dayTimeRangeGroup.length - 1) {
                days.push(StringHelper.toTitleCase(dayTimeRange.day));
                lastStartIndex = dayIndex;
            }
        }
        const firstDayTimeRange = dayTimeRangeGroup[0];
        if (firstDayTimeRange === undefined) {
            throw new Error('Expected a day range');
        }
        const timeStrs = firstDayTimeRange.ranges.map(({ from, to }) => {
            return `${stringTimeFromDayTimeStamp(from)}-${stringTimeFromDayTimeStamp(to)}`;
        });
        return `${days.join(', ')} ${timeStrs.join(', ')}`;
    })
        .value());
}
export function getGmtOffset(timezone) {
    const date = new Date();
    const localeString = date.toLocaleString('en-US', { timeZone: timezone });
    const localDate = new Date(localeString);
    // Calculate the difference in minutes between the local time and UTC
    const offsetMinutes = (localDate.getTime() - date.getTime()) / (1000 * 60);
    // Convert minutes to hours
    const offsetHours = Math.floor(offsetMinutes / 60);
    // Format the offset as GMT+/-X
    const sign = offsetHours >= 0 ? '+' : '-';
    const hours = Math.abs(offsetHours).toString().padStart(2, '0');
    return `GMT${sign}${hours}`;
}
//# sourceMappingURL=time_helper.js.map